import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ChatBot.css';
import chatbotResponses from '../data/chatbot-responses.json';

const ChatBot = () => {
  const { user } = useSelector((state) => state.auth);
  const userName = user?.name || user?.username || 'amigo';
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [lastTopic, setLastTopic] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: `Hola ${userName} 👋`,
      timestamp: new Date(),
    },
    {
      id: 2,
      type: 'bot',
      text: '¿En qué puedo ayudarte hoy?',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus en el input cuando se abre el chat
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Función para calcular similitud entre dos palabras (Levenshtein distance)
  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const getEditDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  // Convertir el JSON a formato de respuestas
  const responses = {};
  Object.values(chatbotResponses).forEach((value) => {
    responses[value.keywords] = value.response
      ? value.response
      : {
        short: value.short,
        detailed: value.detailed,
        related: value.related,
        link: value.link
      };
  });

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase().trim();

    // Detectar si pide más detalles sobre el último tema
    const isShortResponse = lowerMessage.length < 20;
    const wantsDetails = isShortResponse && /^(si|sí|yes|ok|dale|claro|perfecto|detalles|paso a paso|mas|más|pasos)(\s|$)/.test(lowerMessage);

    if (wantsDetails && lastTopic) {
      const topicData = responses[lastTopic];
      if (topicData && topicData.detailed) {
        const detailedText = topicData.detailed + (topicData.related && topicData.related.length > 0
          ? `\n\n🔗 **También:** ${topicData.related.join(', ')}`
          : '');
        const link = topicData.link || null;
        return { text: detailedText, link };
      }
    }

    // Buscar respuesta por palabras clave exactas
    const matchedCandidates = [];
    for (const [keywords, responseData] of Object.entries(responses)) {
      const keywordList = keywords.split('|').map(k => k.trim()).filter(Boolean);
      for (const keyword of keywordList) {
        if (lowerMessage.includes(keyword)) {
          const responseObj = typeof responseData === 'string'
            ? { text: responseData, link: null }
            : {
              text: responseData.short + (responseData.related && responseData.related.length > 0
                ? `\n\n🔗 **También:** ${responseData.related.join(', ')}`
                : ''),
              link: responseData.link || null
            };

          matchedCandidates.push({
            response: responseObj,
            score: keyword.length,
            topic: keywords
          });
          break;
        }
      }
    }

    if (matchedCandidates.length > 0) {
      const top = matchedCandidates.sort((a, b) => b.score - a.score)[0];
      setLastTopic(top.topic);
      return top.response;
    }

    // Busqueda por similitud (fuzzy)
    const words = lowerMessage.split(/\s+/);
    const allKeywords = Object.keys(responses).flatMap(k => k.split('|').map(w => w.trim()));
    const similarityThreshold = 0.65;
    const similarCandidates = [];

    for (const word of words) {
      for (const keyword of allKeywords) {
        const similarity = calculateSimilarity(word, keyword);
        if (similarity >= similarityThreshold) {
          for (const [keys, responseData] of Object.entries(responses)) {
            if (keys.split('|').some(k => k.trim() === keyword)) {
              const responseObj = typeof responseData === 'string'
                ? { text: responseData, link: null }
                : {
                  text: responseData.short + (responseData.related && responseData.related.length > 0
                    ? `\n\n🔗 **También:** ${responseData.related.join(', ')}`
                    : ''),
                  link: responseData.link || null
                };

              const exists = similarCandidates.find(c => c.response.text === responseObj.text);
              if (!exists) similarCandidates.push({ response: responseObj, score: similarity, topic: keys });
            }
          }
        }
      }
    }

    if (similarCandidates.length > 0) {
      const top = similarCandidates.sort((a, b) => b.score - a.score)[0];
      setLastTopic(top.topic);
      return {
        text: `💡 Creo que te refieres a:\n\n${top.response.text}`,
        link: top.response.link
      };
    }

    // Default
    setLastTopic(null);
    return {
      text: 'No encontré algo preciso. Prueba con:\n\n• "crear venta"\n• "agregar producto"\n• "configurar AFIP"\n• "no imprime" (problemas de impresión)\n• "ayuda" (ver menú completo)',
      link: null
    };
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowQuickActions(false);

    setTimeout(() => {
      const response = getBotResponse(inputValue);
      const botResponse = {
        id: messages.length + 2,
        type: 'bot',
        text: response.text,
        link: response.link,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
      setIsLoading(false);
    }, 500);
  };

  const handleQuickAction = (question) => {
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowQuickActions(false);

    setTimeout(() => {
      const response = getBotResponse(question);
      const botResponse = {
        id: messages.length + 2,
        type: 'bot',
        text: response.text,
        link: response.link,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
      setIsLoading(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="chatbot-container">
      {/* Botón flotante */}
      <button
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleChat}
        title="Abrir chat de ayuda"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {/* Ventana del chat */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <img
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=faces&q=95"
                alt="Ximena"
                className="chatbot-avatar"
              />
              <div className="chatbot-header-text">
                <h3>Ximena</h3>
                <p>El equipo también puede ayudar</p>
              </div>
            </div>
            <button
              className="chatbot-close"
              onClick={toggleChat}
              title="Cerrar chat"
            >
              ✕
            </button>
          </div>

          {/* Área de mensajes */}
          <div className="chatbot-messages">
            {showQuickActions && messages.length === 2 && (
              <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button onClick={() => handleQuickAction('¿Cómo crear una venta?')} style={{ padding: '10px', background: '#0088FE', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  💵 Crear venta
                </button>
                <button onClick={() => handleQuickAction('¿Cómo agregar productos?')} style={{ padding: '10px', background: '#00C49F', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  📦 Agregar producto
                </button>
                <button onClick={() => handleQuickAction('¿Cómo configurar AFIP?')} style={{ padding: '10px', background: '#FFBB28', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  🏛️ Configurar AFIP
                </button>
                <button onClick={() => handleQuickAction('No imprime el ticket')} style={{ padding: '10px', background: '#FF8042', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  🖨️ Problema impresión
                </button>
              </div>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.type === 'bot' ? 'bot-message' : 'user-message'}`}
              >
                <div className="message-content">
                  {message.text}
                </div>
                {message.type === 'bot' && message.link && (
                  <button
                    onClick={() => {
                      navigate(message.link);
                      setIsOpen(false);
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '8px 16px',
                      background: '#0088FE',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>🔗 Ir ahora</span>
                  </button>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="message bot-message">
                <div className="message-content">
                  <span className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input área */}
          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Haz una pregunta..."
              rows="1"
            />
            <button
              className="chatbot-send"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
              title="Enviar mensaje"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
